/**
 * 图片压缩工具类
 * 针对低速网络环境优化，支持极致压缩
 */

class ImageCompressor {
  constructor(options = {}) {
    // 默认配置
    this.config = {
      // 最大宽度（像素）
      maxWidth: options.maxWidth || 1920,
      // 最大高度（像素）
      maxHeight: options.maxHeight || 1920,
      // 压缩质量 (0-1)，0.6可以极大减小文件体积
      quality: options.quality || 0.6,
      // 目标格式 'image/jpeg' 或 'image/webp'
      mimeType: options.mimeType || 'image/jpeg',
      // 最大文件大小（KB），超过此大小会进一步压缩
      maxSizeKB: options.maxSizeKB || 500,
      // 是否转换为WebP（更小的文件）
      convertToWebP: options.convertToWebP !== undefined ? options.convertToWebP : false,
      // 缩略图配置
      thumbnail: {
        enabled: options.thumbnail?.enabled || false,
        maxWidth: options.thumbnail?.maxWidth || 200,
        maxHeight: options.thumbnail?.maxHeight || 200,
        quality: options.thumbnail?.quality || 0.5
      }
    }
  }

  /**
   * 压缩图片文件
   * @param {File} file - 原始图片文件
   * @param {Object} options - 压缩选项（可选）
   * @returns {Promise<{file: File, preview: string, originalSize: number, compressedSize: number, ratio: string}>}
   */
  async compress(file, options = {}) {
    return new Promise((resolve, reject) => {
      // 检查文件类型
      if (!file.type.match(/image.*/)) {
        reject(new Error('请选择图片文件'))
        return
      }

      const originalSize = file.size
      const reader = new FileReader()

      reader.onload = async (e) => {
        try {
          const img = new Image()
          img.onload = async () => {
            try {
              // 计算压缩后的尺寸
              const dimensions = this._calculateDimensions(
                img.width,
                img.height,
                options.maxWidth || this.config.maxWidth,
                options.maxHeight || this.config.maxHeight
              )

              // 创建canvas进行压缩
              const canvas = document.createElement('canvas')
              canvas.width = dimensions.width
              canvas.height = dimensions.height
              const ctx = canvas.getContext('2d')

              // 使用更好的图像平滑算法
              ctx.imageSmoothingEnabled = true
              ctx.imageSmoothingQuality = 'high'

              // 绘制图片
              ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height)

              // 确定输出格式
              let mimeType = options.mimeType || this.config.mimeType
              if (this.config.convertToWebP && this._isSupportWebP()) {
                mimeType = 'image/webp'
              }

              // 确定压缩质量
              let quality = options.quality || this.config.quality

              // 转换为Blob
              let blob = await this._canvasToBlob(canvas, mimeType, quality)

              // 如果文件仍然太大，进一步压缩
              const maxSizeKB = options.maxSizeKB || this.config.maxSizeKB
              if (blob.size > maxSizeKB * 1024 && quality > 0.1) {
                blob = await this._furtherCompress(canvas, mimeType, blob.size, maxSizeKB * 1024)
              }

              // 生成预览URL
              const preview = URL.createObjectURL(blob)

              // 创建新的File对象
              const fileName = this._generateFileName(file.name, mimeType)
              const compressedFile = new File([blob], fileName, {
                type: mimeType,
                lastModified: Date.now()
              })

              const compressedSize = compressedFile.size
              const ratio = ((1 - compressedSize / originalSize) * 100).toFixed(1)

              resolve({
                file: compressedFile,
                preview,
                originalSize,
                compressedSize,
                ratio,
                dimensions
              })
            } catch (error) {
              reject(error)
            }
          }

          img.onerror = () => {
            reject(new Error('图片加载失败'))
          }

          img.src = e.target.result
        } catch (error) {
          reject(error)
        }
      }

      reader.onerror = () => {
        reject(new Error('文件读取失败'))
      }

      reader.readAsDataURL(file)
    })
  }

  /**
   * 批量压缩图片
   * @param {File[]} files - 图片文件数组
   * @param {Object} options - 压缩选项
   * @returns {Promise<Array>}
   */
  async compressMultiple(files, options = {}) {
    const promises = Array.from(files).map(file => this.compress(file, options))
    return Promise.all(promises)
  }

  /**
   * 生成缩略图
   * @param {File} file - 原始图片文件
   * @returns {Promise<{file: File, preview: string}>}
   */
  async createThumbnail(file) {
    const thumbOptions = {
      maxWidth: this.config.thumbnail.maxWidth,
      maxHeight: this.config.thumbnail.maxHeight,
      quality: this.config.thumbnail.quality,
      maxSizeKB: 50 // 缩略图最大50KB
    }
    return this.compress(file, thumbOptions)
  }

  /**
   * 计算压缩后的尺寸（保持宽高比）
   * @private
   */
  _calculateDimensions(width, height, maxWidth, maxHeight) {
    let newWidth = width
    let newHeight = height

    // 计算缩放比例
    if (width > maxWidth) {
      newWidth = maxWidth
      newHeight = (height * maxWidth) / width
    }

    if (newHeight > maxHeight) {
      newHeight = maxHeight
      newWidth = (width * maxHeight) / height
    }

    return {
      width: Math.round(newWidth),
      height: Math.round(newHeight)
    }
  }

  /**
   * Canvas转Blob
   * @private
   */
  _canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob)
        },
        mimeType,
        quality
      )
    })
  }

  /**
   * 进一步压缩（如果文件仍然太大）
   * @private
   */
  async _furtherCompress(canvas, mimeType, currentSize, targetSize) {
    let quality = 0.9
    let blob = null
    let attempts = 0
    const maxAttempts = 10

    while (attempts < maxAttempts) {
      blob = await this._canvasToBlob(canvas, mimeType, quality)

      if (blob.size <= targetSize || quality <= 0.1) {
        break
      }

      // 根据当前大小调整质量
      const ratio = targetSize / blob.size
      quality = Math.max(0.1, quality * ratio * 0.9)
      attempts++
    }

    return blob
  }

  /**
   * 检查浏览器是否支持WebP
   * @private
   */
  _isSupportWebP() {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
  }

  /**
   * 生成新的文件名
   * @private
   */
  _generateFileName(originalName, mimeType) {
    const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName
    const extension = mimeType.split('/')[1]
    return `${nameWithoutExt}_compressed.${extension}`
  }

  /**
   * 获取图片信息（不压缩）
   * @param {File} file - 图片文件
   * @returns {Promise<Object>}
   */
  async getImageInfo(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
            size: file.size,
            type: file.type,
            name: file.name
          })
        }
        img.onerror = () => reject(new Error('无法读取图片信息'))
        img.src = e.target.result
      }
      reader.onerror = () => reject(new Error('文件读取失败'))
      reader.readAsDataURL(file)
    })
  }
}

// 导出单例
export default new ImageCompressor()

// 也导出类，以便自定义配置
export { ImageCompressor }
